'use client';

import { useState, useMemo } from 'react';
import { Search, Plus, Eye, Edit2, Phone, Mail, Calendar, FileText, CheckCircle2, Clock } from 'lucide-react';
import { DataTable } from '../../../components/shared/DataTable';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { activities, leads, customers, opportunities } from '../data/mock-data';
import { activityTypeConfig, activityStatusConfig, type Activity, type ActivityType, type ActivityStatus } from '../types';

// 활동 등록 모달
function ActivityAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [selectedRelatedTo, setSelectedRelatedTo] = useState<'lead' | 'customer' | 'opportunity' | ''>('');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="활동 등록"
      description="새로운 영업 활동을 등록합니다."
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button onClick={onClose}>등록</Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <FormField label="활동 유형" required>
            <FormSelect>
              <option value="">선택하세요</option>
              {Object.entries(activityTypeConfig).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="제목" required>
            <FormInput placeholder="활동 제목을 입력하세요" />
          </FormField>
          <FormField label="관련 대상" required>
            <FormSelect
              value={selectedRelatedTo}
              onChange={(e) => setSelectedRelatedTo(e.target.value as 'lead' | 'customer' | 'opportunity' | '')}
            >
              <option value="">선택하세요</option>
              <option value="lead">리드</option>
              <option value="customer">고객</option>
              <option value="opportunity">영업기회</option>
            </FormSelect>
          </FormField>
          <FormField label="관련 항목" required>
            <FormSelect disabled={!selectedRelatedTo}>
              <option value="">선택하세요</option>
              {selectedRelatedTo === 'lead' && leads.map((lead) => (
                <option key={lead.id} value={lead.id}>{lead.name}</option>
              ))}
              {selectedRelatedTo === 'customer' && customers.map((customer) => (
                <option key={customer.id} value={customer.id}>{customer.name}</option>
              ))}
              {selectedRelatedTo === 'opportunity' && opportunities.map((opp) => (
                <option key={opp.id} value={opp.id}>{opp.name}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="예정일">
            <FormInput type="date" />
          </FormField>
          <FormField label="담당자">
            <FormSelect>
              <option value="">선택하세요</option>
              <option value="u001">김영업</option>
              <option value="u002">이마케팅</option>
              <option value="u003">박관리</option>
            </FormSelect>
          </FormField>
        </div>
        <FormField label="설명">
          <FormTextarea rows={3} placeholder="활동에 대한 설명을 입력하세요" />
        </FormField>
      </div>
    </Modal>
  );
}

export function ActivityListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return activities.filter((activity) => {
      const matchesSearch =
        activity.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        activity.relatedName.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesType = selectedType === 'all' || activity.type === selectedType;
      const matchesStatus = selectedStatus === 'all' || activity.status === selectedStatus;

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [searchQuery, selectedType, selectedStatus]);

  // 상태별 통계
  const statusStats = useMemo(() => {
    const stats: Record<string, number> = {};
    activities.forEach((activity) => {
      stats[activity.status] = (stats[activity.status] || 0) + 1;
    });
    return stats;
  }, []);

  // 유형별 통계
  const typeStats = useMemo(() => {
    const stats: Record<string, number> = {};
    activities.forEach((activity) => {
      stats[activity.type] = (stats[activity.type] || 0) + 1;
    });
    return stats;
  }, []);

  const getStatusColor = (color: string) => {
    const colors: Record<string, string> = {
      blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      gray: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
    };
    return colors[color] || colors.gray;
  };

  const getTypeIcon = (type: ActivityType) => {
    switch (type) {
      case 'call':
        return Phone;
      case 'email':
        return Mail;
      case 'meeting':
        return Calendar;
      case 'task':
        return CheckCircle2;
      case 'note':
        return FileText;
      default:
        return FileText;
    }
  };

  const columns = [
    {
      key: 'type',
      label: '유형',
      width: '12%',
      render: (value: unknown) => {
        const type = value as ActivityType;
        const config = activityTypeConfig[type];
        const Icon = getTypeIcon(type);
        return (
          <div className="flex items-center gap-2 whitespace-nowrap">
            <div className={`p-1.5 rounded flex-shrink-0 ${getStatusColor(config.color)}`}>
              <Icon className="w-3 h-3" />
            </div>
            <span className="text-sm">{config.label}</span>
          </div>
        );
      },
    },
    {
      key: 'subject',
      label: '제목',
      width: '28%',
      sortable: true,
      render: (value: unknown, row: Activity) => (
        <div className="min-w-0">
          <div className="font-medium truncate">{String(value)}</div>
          <div className="text-xs text-muted-foreground truncate">{row.relatedName}</div>
        </div>
      ),
    },
    {
      key: 'scheduledDate',
      label: '예정일',
      width: '12%',
      sortable: true,
      render: (value: unknown) =>
        value ? (
          <div className="flex items-center gap-1 text-sm whitespace-nowrap">
            <Clock className="w-3 h-3 text-muted-foreground flex-shrink-0" />
            {String(value)}
          </div>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      key: 'completedDate',
      label: '완료일',
      width: '12%',
      render: (value: unknown) =>
        value ? (
          <div className="flex items-center gap-1 text-sm whitespace-nowrap">
            <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0" />
            {String(value)}
          </div>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      key: 'assignedToName',
      label: '담당자',
      width: '10%',
      render: (value: unknown) => (
        <span className="text-sm whitespace-nowrap">{String(value || '-')}</span>
      ),
    },
    {
      key: 'status',
      label: '상태',
      width: '10%',
      render: (value: unknown) => {
        const status = value as ActivityStatus;
        const config = activityStatusConfig[status];
        return (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(config.color)}`}>
            {config.label}
          </span>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      width: '8%',
      render: () => (
        <div className="flex items-center gap-1">
          <button className="p-1.5 rounded hover:bg-accent" title="보기">
            <Eye className="w-4 h-4 text-muted-foreground" />
          </button>
          <button className="p-1.5 rounded hover:bg-accent" title="수정">
            <Edit2 className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="활동 관리"
        description="영업 활동을 조회하고 관리합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            활동 등록
          </button>
        }
      />

      {/* 요약 카드 */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {Object.entries(activityStatusConfig).map(([status, config]) => (
          <button
            key={status}
            onClick={() => setSelectedStatus(selectedStatus === status ? 'all' : status)}
            className={`p-4 rounded-lg border text-left transition-colors ${
              selectedStatus === status
                ? 'border-primary bg-primary/5'
                : 'border-border bg-card hover:bg-accent/50'
            }`}
          >
            <div className="text-sm text-muted-foreground">{config.label}</div>
            <div className="text-2xl font-bold mt-1">{statusStats[status] || 0}건</div>
          </button>
        ))}
      </div>

      {/* 유형별 요약 */}
      <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
        {Object.entries(activityTypeConfig).map(([type, config]) => {
          const Icon = getTypeIcon(type as ActivityType);
          return (
            <button
              key={type}
              onClick={() => setSelectedType(selectedType === type ? 'all' : type)}
              className={`p-3 rounded-lg border text-center transition-colors ${
                selectedType === type
                  ? 'border-primary bg-primary/5'
                  : 'border-border bg-card hover:bg-accent/50'
              }`}
            >
              <div className={`inline-flex p-2 rounded-lg mb-2 ${getStatusColor(config.color)}`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="text-xs text-muted-foreground">{config.label}</div>
              <div className="text-lg font-bold">{typeStats[type] || 0}</div>
            </button>
          );
        })}
      </div>

      {/* 필터 */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="제목, 관련 항목 검색..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 유형</option>
          {Object.entries(activityTypeConfig).map(([type, config]) => (
            <option key={type} value={type}>
              {config.label}
            </option>
          ))}
        </select>

        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 상태</option>
          {Object.entries(activityStatusConfig).map(([status, config]) => (
            <option key={status} value={status}>
              {config.label}
            </option>
          ))}
        </select>
      </div>

      {/* 데이터 테이블 */}
      <DataTable
        data={filteredData}
        columns={columns}
        pageSize={10}
        emptyMessage="검색 결과가 없습니다."
      />

      {/* 활동 등록 모달 */}
      <ActivityAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
